import { Dispatch, useCallback, useEffect, useRef, useState } from "react";
import Hls, { LevelParsed, ManifestLoadedData } from "hls.js";

import { HLS_CONFIG_PARAMS } from "../../constants";
import * as Constants from "../../constants";
import { useEmbedParametersContext } from "../../contexts/embedParametersContext";
import { useMediaContext } from "../../contexts/MediaContext";
import { isDebugEnabled } from "../../helpers/debug";
import {
  mapPlayerVideoTracks,
  qualityLabel
} from "../../helpers/videoQualityLevels";
import { usePlayerStore } from "../../store/usePlayerStore";
import { AudioTrack } from "../../types/common";
import useDocumentVisible from "../useDocumentVisible/useDocumentVisible";

const getMatchingHlsLevel = (videoTrack, hls): LevelParsed | undefined => {
  const targetResolution = parseInt(videoTrack.label);
  return hls.levels.reduce(
    (previousValue, currentValue) =>
      Math.min(currentValue.height, currentValue.width) === targetResolution &&
      (!previousValue || currentValue.bitrate > previousValue.bitrate)
        ? currentValue
        : previousValue,
    undefined
  );
};

const getClosestHlsLevel = (levels, targetQuality) =>
  levels.reduce((accIndex, currentLevel, currentIndex) => {
    const accLevel = levels[accIndex];
    const currentQuality = Math.min(currentLevel.height, currentLevel.width);
    const accQuality = Math.min(accLevel.height, accLevel.width);
    return currentQuality &&
      accQuality &&
      currentQuality > accQuality &&
      currentQuality <= targetQuality
      ? currentIndex
      : accIndex;
  }, 0);

const maxRetryNum = 4;

const useSetupHlsJs = ({
  destroy,
  setIsMediaAttached,
  subtitleDisplay = false,
  usingServiceScheduleTracks,
  video
}: {
  destroy: boolean;
  setIsMediaAttached: Dispatch<boolean>;
  subtitleDisplay?: boolean;
  usingServiceScheduleTracks: boolean;
  video?: HTMLVideoElement;
}) => {
  const { getState, useStoreState } = usePlayerStore();

  const audioTracks = useStoreState.audioTracks();
  const device = useStoreState.device();
  const isAdaptiveBitrate = useStoreState.isAdaptiveBitrate();
  const isLive = useStoreState.isLive();
  const isPlaybackStarted = useStoreState.isPlaybackStarted();
  const isPlayerSetupStarted = useStoreState.isPlayerSetupStarted();
  const player = useStoreState.player();
  const selectedAudioTrackId = useStoreState.selectedAudioTrackId();
  const selectedSubtitles = useStoreState.selectedSubtitles();
  const userPreferredVideoTrack = useStoreState.userPreferredVideoTrack();
  const isPlayerSetupDone = useStoreState.isPlayerSetupDone();
  const snowplowEvents = useStoreState.snowplowEvents();

  const setAudioTracks = useStoreState.setAudioTracks();
  const setError = useStoreState.setError();
  const setHlsJs = useStoreState.setHlsJs();
  const setIsAdaptiveBitrate = useStoreState.setIsAdaptiveBitrate();
  const setIsPlayerSetupDone = useStoreState.setIsPlayerSetupDone();
  const setSelectedVideoTrack = useStoreState.setSelectedVideoTrack();
  const setVideoTracks = useStoreState.setVideoTracks();

  const { manifestUrl } = useMediaContext();
  const { optimizeStartLevel, preload } = useEmbedParametersContext();

  const [fragmentLoadingStarted, setFragmentLoadingStarted] =
    useState<boolean>(false);
  const [manifestLoaded, setManifestLoaded] = useState<boolean>(false);
  const [quality, setQuality] = useState<number>();

  const hlsRef = useRef<Hls | null>(null);
  const mediaErrorStatusRef = useRef<"noError" | "resolving" | "resolvedOnce">(
    "noError"
  );
  const retryNum = useRef<number>(0);

  const isChrome = !!navigator.userAgent.match(/chrome|chromium/i);

  const documentVisible = useDocumentVisible();

  const onManifestLoaded = useCallback(
    (payload: ManifestLoadedData) => {
      setVideoTracks(
        mapPlayerVideoTracks(
          payload.levels.sort((a, b) => a.bitrate - b.bitrate)
        )
      );
      setManifestLoaded(true);
      if (hlsRef.current && mediaErrorStatusRef.current === "resolving") {
        mediaErrorStatusRef.current = "resolvedOnce";
      }
    },
    [setVideoTracks]
  );

  useEffect(() => {
    if (isAdaptiveBitrate && hlsRef.current) {
      hlsRef.current.currentLevel = -1;
    }
  }, [isAdaptiveBitrate]);

  useEffect(() => {
    const hls = hlsRef.current;
    if (manifestLoaded && hls?.levels && userPreferredVideoTrack) {
      let hlsLevel;
      if (userPreferredVideoTrack.bitrate !== "auto") {
        hlsLevel = getMatchingHlsLevel(userPreferredVideoTrack, hls);
        if (hlsLevel) {
          const currentLevelIndex = hls.levels.indexOf(hlsLevel);
          setIsAdaptiveBitrate(false);
          setSelectedVideoTrack({
            index: currentLevelIndex,
            label: qualityLabel(hlsLevel)
          });
          hls.currentLevel = currentLevelIndex;
        }
      }

      if (userPreferredVideoTrack.bitrate === "auto" || !hlsLevel) {
        setSelectedVideoTrack({ index: -1, label: "auto" });
        setIsAdaptiveBitrate(true);
      }
    }
  }, [
    manifestLoaded,
    setIsAdaptiveBitrate,
    setSelectedVideoTrack,
    userPreferredVideoTrack
  ]);

  useEffect(() => {
    // Fragment loading has to be stopped before switching to another tab/app that has media playback to prevent any problems
    if (device.isMobile && isLive && video && isChrome) {
      if (documentVisible) {
        hlsRef.current?.startLoad();
      } else {
        hlsRef.current?.stopLoad();
        player.pause();
      }
    }
  }, [documentVisible, isChrome, isLive, device, player, video]);

  useEffect(() => {
    if (
      hlsRef.current &&
      isPlayerSetupDone &&
      selectedAudioTrackId !== undefined &&
      selectedAudioTrackId !== -1
    ) {
      hlsRef.current.audioTrack = selectedAudioTrackId;
    }
  }, [audioTracks, selectedAudioTrackId, video, isPlayerSetupDone]);

  useEffect(() => {
    if (isPlaybackStarted && fragmentLoadingStarted && hlsRef.current) {
      hlsRef.current.config.maxMaxBufferLength =
        Constants.HLS_CONFIG_PARAMS.MAX_MAX_BUFFER_LENGTH;

      setIsPlayerSetupDone(true);
    }
  }, [fragmentLoadingStarted, isPlaybackStarted, player, setIsPlayerSetupDone]);

  useEffect(() => {
    if (!isPlayerSetupStarted) {
      setFragmentLoadingStarted(false);
    }
  }, [isPlayerSetupStarted]);

  useEffect(() => {
    if (
      (isPlaybackStarted || preload === "auto") &&
      manifestLoaded &&
      hlsRef.current &&
      !fragmentLoadingStarted
    ) {
      if (optimizeStartLevel || quality) {
        hlsRef.current.config.startLevel = getClosestHlsLevel(
          hlsRef.current.levels,
          quality || HLS_CONFIG_PARAMS.PREFERRED_START_LEVEL_QUALITY
        );
      }
      hlsRef.current.startLoad(getState().currentTime);
      setFragmentLoadingStarted(true);
    }
  }, [
    getState,
    fragmentLoadingStarted,
    isPlaybackStarted,
    manifestLoaded,
    optimizeStartLevel,
    preload,
    quality
  ]);

  // Required for Safari
  useEffect(() => {
    if (hlsRef.current && getState().isPlaybackStarted) {
      hlsRef.current.startLoad();
    }
  }, [getState, selectedSubtitles]);

  useEffect(() => {
    if (destroy) {
      hlsRef.current?.destroy();
    }
  }, [destroy]);

  return () => {
    hlsRef.current?.destroy();

    const hls = new Hls({
      autoStartLoad: false,
      debug: isDebugEnabled("hlsjs"),
      ...(preload === "auto" && {
        maxMaxBufferLength:
          Constants.HLS_CONFIG_PARAMS.MAX_MAX_BUFFER_LENGTH_PRELOAD
      })
    });

    setHlsJs({
      startLoad: () => hls.startLoad(),
      stopLoad: () => hls.stopLoad()
    });

    hls.subtitleDisplay = subtitleDisplay;
    hls.on(Hls.Events.ERROR, (_, { details, fatal, type }) => {
      if (
        fatal &&
        type === Hls.ErrorTypes.MEDIA_ERROR &&
        mediaErrorStatusRef.current === "noError"
      ) {
        console.warn("Hls media error, trying to recover");
        mediaErrorStatusRef.current = "resolving";
        hls.recoverMediaError();
      } else if (fatal) {
        if (retryNum.current < maxRetryNum) {
          retryNum.current += 1;
          setTimeout(() => {
            console.warn("Retry no. ", retryNum.current);
            hlsRef.current?.stopLoad();
            hlsRef.current?.startLoad();
          }, 1000);
        } else {
          console.error("Hls media error", details);
          setError({ details, type });
        }
      }
    });
    if (video) {
      const originalPlaybackRate = video.playbackRate;
      hls.attachMedia(video);
      video.playbackRate = originalPlaybackRate;
    }
    if (!usingServiceScheduleTracks) {
      hls.on(Hls.Events.AUDIO_TRACKS_UPDATED, (_, payload) => {
        const tracks: AudioTrack[] = [];
        for (const track of payload.audioTracks) {
          tracks.push({
            id: track.id,
            label: track.name,
            language: track.lang
          });
        }
        setAudioTracks(tracks);
      });
    }

    hls.on(Hls.Events.MANIFEST_LOADED, (_, payload) => {
      onManifestLoaded(payload);
    });

    hls.on(Hls.Events.MEDIA_DETACHING, () => setManifestLoaded(false));

    hls.on(Hls.Events.MEDIA_ATTACHED, () => {
      setIsMediaAttached(true);
      if (manifestUrl) {
        hls.loadSource(manifestUrl);
      }
    });

    hls.on(Hls.Events.LEVEL_SWITCHED, (_, payload) => {
      const hlsLevel = hls.levels[payload.level];
      setQuality(Math.min(hlsLevel.height, hlsLevel.width));
      setSelectedVideoTrack({
        index: payload.level,
        label: qualityLabel(hlsLevel)
      });
      snowplowEvents?.onQualityChange({
        automatic: getState().isAdaptiveBitrate,
        bitrate: hlsLevel.bitrate,
        framerPerSecond: hlsLevel.frameRate,
        newQuality: qualityLabel(hlsLevel)
      });
    });

    hlsRef.current = hls;

    return hls;
  };
};

export default useSetupHlsJs;
